import React, { useEffect, useMemo, useState } from "react";
import { Box, Divider, Pagination, Stack, Typography, TextField, IconButton, Button, Skeleton, Alert } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import { useSite } from "../../context/siteContext";
import { useAuth } from "../../context/authContext";
import TopProductListItem from "../../components/TopProductListItem/TopProductListItem";
import MachineListItem from "../../components/MachineListItem/MachineListItem";
import PageListItem from "../../components/PageListItem/PageListItem";
import OrderListItem from "../../components/OrderListItem/OrderListItem";
import { DOCUMENT_EXTERNAL_ENABLED, DOCUMENT_EXTERNAL_URL, SERVER_BASE_URL } from "../../config";
// import GlobalSearch from "../../components/GlobalSearch/GlobalSearch";
import PageTitle from "../../components/PageTitle/PageTitle";
import KPICard from "../../components/KPICard/KPICard";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import BuildIcon from "@mui/icons-material/Build";
import LinkIcon from "@mui/icons-material/Link";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DescriptionIcon from "@mui/icons-material/Description";
import { useTranslation } from "react-i18next";
import { searchProducts } from "../../services/globalSearchService";
import { getProductsById } from "../../services/cartService"
import DocCard from "../../components/DocCard/DocCard";
import { Note } from "@mui/icons-material";
import useAccessiblePages from "../../hooks/useAccessiblePages";
import { useTheme } from "@mui/material/styles";
import { ROUTES } from "../../constants";
import { buildExternalLoginUrl } from "../../utilities/externalDocs";

const useQuery = () => new URLSearchParams(useLocation().search);

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const getDocSubtitle = (doc, t) => {
  const machines = parseJsonArray(doc?.machine_details)
    .map((machine) => machine?.machine_number || machine?.machine_name)
    .filter(Boolean);
  const categories = parseJsonArray(doc?.categories)
    .map((cat) => cat?.category_name)
    .filter(Boolean);

  const formatList = (values) => {
    const limit = 2;
    if (values.length <= limit) return values.join(", ");
    return `${values.slice(0, limit).join(", ")} +${values.length - limit} more`;
  };

  const machineLabel = t('UI_TEXT.MACHINES') || 'Machines';
  const rawCategoryLabel = t('UI_TEXT.CATEGORIES');
  const categoryLabel = rawCategoryLabel && rawCategoryLabel !== 'UI_TEXT.CATEGORIES' ? rawCategoryLabel : 'Categories';

  const parts = [];
  if (machines.length) parts.push(`${machineLabel}: ${formatList(machines)}`);
  if (categories.length) parts.push(`${categoryLabel}: ${formatList(categories)}`);
  return parts.join(" • ");
};

const getDocHref = (doc) => {
  const type = String(doc?.attachment_type || "").toLowerCase();
  if (type === "url" && doc?.attachment) return doc.attachment;
  if (type === "file" && doc?.attachment) return `${SERVER_BASE_URL}/${doc.attachment}`;
  return "";
};

const getDocChipLabel = (doc) => {
  const type = String(doc?.attachment_type || "").toLowerCase();
  if (type === "url") return "URL";
  if (type === "file") {
    const path = doc?.attachment || doc?.name || "";
    const ext = (path.split(".").pop() || "").toLowerCase();
    if (!ext) return "FILE";
    const map = {
      pdf: "PDF",
      png: "PNG",
      jpg: "JPG",
      jpeg: "JPG",
      csv: "CSV",
      xls: "Excel",
      xlsx: "Excel",
      doc: "Word",
      docx: "Word",
      ppt: "PPT",
      pptx: "PPT",
    };
    return map[ext] || ext.toUpperCase();
  }
  return "DOC";
};

const matchesPageSearch = (page, term) => {
  const normalizedTerm = String(term || "").trim().toLowerCase();
  if (!normalizedTerm) return true;
  const haystack = [
    page?.name,
    ...(Array.isArray(page?.searchTerms) ? page.searchTerms : []),
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());
  return haystack.some((value) => value.includes(normalizedTerm));
};

const SearchResults = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const q = query.get("q") || "";
  const partsOnly =
    String(query.get("partsonly") || "").toLowerCase() === "true" ||
    query.get("partsonly") === "1";
  const { selectedSite, selectedSiteData } = useSite();
  const { user } = useAuth();
  const accessiblePages = useAccessiblePages();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [products, setProducts] = useState([]);
  const { t } = useTranslation();
  const [search, setSearch] = useState(q);
  const [activeCategories, setActiveCategories] = useState([]);

  const pageResults = useMemo(() => {
    if (partsOnly) return [];
    const s = q.toLowerCase();
    return accessiblePages.filter((page) => matchesPageSearch(page, s));
  }, [q, accessiblePages, partsOnly]);

  useEffect(() => {
    setSearch(q || "");
  }, [q]);

  const handleDocumentationNavigate = () => {
    if (!DOCUMENT_EXTERNAL_ENABLED || !DOCUMENT_EXTERNAL_URL) {
      navigate(ROUTES.DOCUMENT);
      return;
    }
    const username = selectedSiteData?.amf_method_username_ln;
    const password = selectedSiteData?.amf_method_password_ln;
    const loginUrl = buildExternalLoginUrl({
      templateUrl: DOCUMENT_EXTERNAL_URL,
      username,
      password,
    });
    if (!loginUrl) {
      navigate(ROUTES.DOCUMENT);
      return;
    }
    window.open(loginUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    const run = async () => {
      if (!selectedSite || q.length < 2) {
        setResults([]);
        setProducts([]);
        return;
      }
      setLoading(true);
      try {
        const resp = await searchProducts({
          site_number: selectedSite,
          searchTerm: q,
          ...(user?.user_id ? { user_id: user.user_id } : {}),
        });
        const data = Array.isArray(resp?.data) ? resp.data.map((item) =>
          item?.category === "Documents"
            ? {
              ...item,
              categories: parseJsonArray(item.categories),
              machine_details: parseJsonArray(item.machine_details),
            }
            : item
        ) : [];
        const filteredData = partsOnly
          ? data.filter((item) => item.category === "Products" || item.category === "Machines")
          : data;

        // Fetch full product details for Products category using /products/byids
        const productIds = Array.from(new Set(
          filteredData.filter(d => d.category === "Products").map(d => d.product_id)
        ));
        let fullProducts = [];
        if (productIds.length > 0) {
          const prodResp = await getProductsById(productIds, selectedSite);
          if (prodResp?.status) {
            fullProducts = prodResp.data;
          }
        }

        setProducts(fullProducts);
        setResults(filteredData);
      } catch (e) {
        console.error("Search failed:", e);
        setResults([]);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [q, selectedSite, user?.user_id, partsOnly]);

  const triggerSearch = () => {
    if (!search) return;
    const params = new URLSearchParams();
    params.set("q", search);
    if (partsOnly) params.set("partsonly", "true");
    navigate(`/search-results?${params.toString()}`);
  };

  const clearResults = () => {
    setSearch("");
    setResults([]);
    setProducts([]);
    setActiveCategories([]);
    if (partsOnly) {
      navigate(`/search-results?partsonly=true`);
      return;
    }
    navigate(`/search-results`);
  };

  // Build product map early to allow filtering out unresolved product items for accurate pagination
  const productMap = useMemo(() => {
    const m = new Map();
    for (const p of products) m.set(p.product_id, p);
    return m;
  }, [products]);

  const documentsList = useMemo(() => results.filter(r => r.category === "Documents"), [results]);

  const documentCategories = useMemo(() => {
    if (!documentsList.length) return [];
    const map = new Map();
    const fallbackName = t('UI_TEXT.UNCATEGORIZED') || 'Uncategorized';
    documentsList.forEach((doc) => {
      const categories = Array.isArray(doc.categories) ? doc.categories : [];
      if (!categories.length) {
        const entry = map.get(fallbackName) || { name: fallbackName, count: 0 };
        entry.count += 1;
        map.set(fallbackName, entry);
        return;
      }
      categories.forEach((cat) => {
        const name = (cat?.category_name || fallbackName).trim();
        if (!name) return;
        const entry = map.get(name) || { name, count: 0 };
        entry.count += 1;
        map.set(name, entry);
      });
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [documentsList, t]);

  // Ensure Products section only counts items that can be rendered (i.e., have full product details)
  const categorized = {
    Machines: results.filter(r => r.category === "Machines"),
    Products: results.filter(r => r.category === "Products" && productMap.has(r.product_id)),
    Orders: results.filter(r => r.category === "Orders"),
    Pages: pageResults,
    Documents: documentsList,
    DocumentCategories: documentCategories,
  };

  // Pagination state per category
  const pageSize = 5;
  const [machinesPage, setMachinesPage] = useState(1);
  const [productsPage, setProductsPage] = useState(1);
  const [ordersPage, setOrdersPage] = useState(1);
  const [pagesPage, setPagesPage] = useState(1);
  const [documentsPage, setDocumentsPage] = useState(1);
  const [documentCategoriesPage, setDocumentCategoriesPage] = useState(1);

  useEffect(() => {
    // Reset pagination and filters when query or site changes
    setMachinesPage(1);
    setProductsPage(1);
    setOrdersPage(1);
    setPagesPage(1);
    setDocumentsPage(1);
    setDocumentCategoriesPage(1);
    setActiveCategories([]);
  }, [q, selectedSite, partsOnly]);

  const paginate = (arr, page) => arr.slice((page - 1) * pageSize, page * pageSize);
  const machinesPaginated = paginate(categorized.Machines, machinesPage);
  const productsPaginated = paginate(categorized.Products, productsPage);
  const ordersPaginated = paginate(categorized.Orders, ordersPage);
  const pagesPaginated = paginate(categorized.Pages, pagesPage);
  const documentsPaginated = paginate(categorized.Documents, documentsPage);
  const documentCategoriesPaginated = paginate(categorized.DocumentCategories, documentCategoriesPage);

  const nonEmptySections = [
    { key: 'Machines', items: categorized.Machines, itemsPage: machinesPaginated, page: machinesPage, setPage: setMachinesPage },
    { key: 'Products', items: categorized.Products, itemsPage: productsPaginated, page: productsPage, setPage: setProductsPage },
    { key: 'Orders', items: categorized.Orders, itemsPage: ordersPaginated, page: ordersPage, setPage: setOrdersPage },
    { key: 'Documents', items: categorized.Documents, itemsPage: documentsPaginated, page: documentsPage, setPage: setDocumentsPage },
    { key: 'DocumentCategories', items: categorized.DocumentCategories, itemsPage: documentCategoriesPaginated, page: documentCategoriesPage, setPage: setDocumentCategoriesPage },
    { key: 'Pages', items: categorized.Pages, itemsPage: pagesPaginated, page: pagesPage, setPage: setPagesPage },
  ].filter(sec => sec.items.length > 0);

  const filteredSections = activeCategories.length
    ? nonEmptySections.filter((sec) => activeCategories.includes(sec.key))
    : nonEmptySections;

  // Form rows of two sections each based on availability
  const rows = [];
  for (let i = 0; i < filteredSections.length; i += 2) {
    rows.push(filteredSections.slice(i, i + 2));
  }

  // productMap declared above

  const handleCategoryToggle = (key) => {
    setActiveCategories((prev) =>
      prev.includes(key) ? prev.filter((cat) => cat !== key) : [...prev, key]
    );
  };

  const clearCategoryFilters = () => setActiveCategories([]);

  const handleDocumentCategoryNavigate = (categoryName) => {
    if (!categoryName) return;
    navigate(`/documents?category=${encodeURIComponent(categoryName)}`);
  };

  const kpiCards = partsOnly
    ? [
      { key: 'Machines', label: t('UI_TEXT.MACHINES'), value: categorized.Machines.length, color: "#0ea5e9", icon: PrecisionManufacturingIcon },
      { key: 'Products', label: t('UI_TEXT.PARTS'), value: categorized.Products.length, color: "#22c55e", icon: BuildIcon },
    ]
    : [
      { key: 'Machines', label: t('UI_TEXT.MACHINES'), value: categorized.Machines.length, color: "#0ea5e9", icon: PrecisionManufacturingIcon },
      { key: 'Products', label: t('UI_TEXT.PARTS'), value: categorized.Products.length, color: "#22c55e", icon: BuildIcon },
      { key: 'Pages', label: t('UI_TEXT.PAGES'), value: categorized.Pages.length, color: "#a855f7", icon: LinkIcon },
      { key: 'Documents', label: t('UI_TEXT.DOCUMENTS'), value: categorized.Documents.length, color: "#6366f1", icon: DescriptionIcon },
      { key: 'DocumentCategories', label: t('UI_TEXT.DOCUMENT_CATEGORIES'), value: categorized.DocumentCategories.length, color: "#ec4899", icon: DescriptionIcon },
      { key: 'Orders', label: t('LABELS.ORDER'), value: categorized.Orders.length, color: "#f59e0b", icon: ReceiptLongIcon },
    ];

  const trimmedQuery = q.trim();
  const subtitleText = trimmedQuery
    ? `Search results for "${trimmedQuery}"`
    : t("UI_TEXT.PLEASE_START_TYPING_TO_SEARCH");
  const subtitle = (
    <span style={{ color: theme.palette.text.secondary }}>{subtitleText}</span>
  );
  const showEmptyPrompt = !loading && trimmedQuery.length < 2;
  const showNoResults = !loading && trimmedQuery.length >= 2 && filteredSections.length === 0;

  return (
    <Box sx={{ p: 2, width: '90vw', mx: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', height: '100%', minHeight: '650px' }}>
      <PageTitle title={partsOnly ? t("LABELS.SEARCH_PARTS") : t("UI_TEXT.SEARCH_RESULTS")} subheader={subtitle} />
      <Box sx={{ mb: 2, display: 'flex', columnGap: 1 }}>
        <TextField
          variant="outlined"
          placeholder={t('PLACEHOLDER.SEARCHS')}
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') triggerSearch(); }}
          size="small"
          slotProps={{
            input: {
              endAdornment: (
                <Box display="flex">
                  <IconButton onClick={triggerSearch}>
                    <SearchIcon />
                  </IconButton>
                </Box>
              ),
            },
          }}
          sx={{
            "& .MuiInputBase-root": { paddingRight: "2px" },
            "& .MuiOutlinedInput-root": { borderRadius: "40px", backgroundColor: "#fff" },
            "& .MuiInputBase-input::placeholder": {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            width: "100%",
          }}
        />
        <Button variant="outlined" onClick={clearResults}>
          {t('BUTTON.CLEAR_RESULTS')}
        </Button>
      </Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        {t("UI_TEXT.SEARCH_HELPER_TEXT")}
      </Typography>

      {/* KPI Row */}
      <Stack
        direction="row"
        spacing={2}
        sx={{
          mb: 1,
          py: 1,
          px: 1,
          flexWrap: 'nowrap',     // ❌ no wrapping
          width: '100%',
          overflowX: 'auto',
        }}
      >

        {kpiCards.map((card) => (

          <KPICard
            key={card.key}
            label={card.label}
            value={card.value}
            color={card.color}
            icon={card.icon}
            active={activeCategories.includes(card.key)}
            onActionClick={() => handleCategoryToggle(card.key)}
          />

        ))}
      </Stack>
      {activeCategories.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="text" onClick={clearCategoryFilters}>
            {t('BUTTON.CLEAR_FILTERS') || 'Clear Filters'}
          </Button>
        </Box>
      )}

      {loading ? (
        <Box sx={{ width: "100%", mt: 3 }}>
          <Skeleton variant="text" width="40%" height={32} />
          <Skeleton variant="rectangular" height={120} sx={{ my: 2 }} />
          <Skeleton variant="text" width="60%" height={28} />
          <Skeleton variant="rectangular" height={180} sx={{ my: 2 }} />
        </Box>
      ) : showEmptyPrompt ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t("UI_TEXT.PLEASE_START_TYPING_TO_SEARCH")}
        </Alert>
      ) : showNoResults ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t("UI_TEXT.NO_RESULT_FOUND")}
        </Alert>
      ) : (
        rows.map((row, rowIdx) => (
          <Box key={`row-${rowIdx}`} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', columnGap: 2, alignItems: 'stretch' }}>
              {row.map((sec) => (
                <Box key={`sec-${rowIdx}-${sec.key}`} sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, mt: 1, fontWeight: 600 }}>
                  {sec.key === 'Machines' && t('UI_TEXT.MACHINES')}
                  {sec.key === 'Products' && t('UI_TEXT.PARTS')}
                  {sec.key === 'Pages' && t('UI_TEXT.PAGES')}
                  {sec.key === 'Orders' && t('LABELS.ORDER')}
                  {sec.key === 'Documents' && t('UI_TEXT.DOCUMENTS')}
                  {sec.key === 'DocumentCategories' && t('UI_TEXT.DOCUMENT_CATEGORIES')}
                </Typography>
                {/* Items - force re-mount per page to avoid accumulation */}
                <Box key={`items-${sec.key}-${sec.page}`}>
                  {sec.key === 'Machines' && sec.itemsPage.map((m, idx) => (
                    <MachineListItem key={`mach-${m.machine_id}-${idx}-p${sec.page}`} machine={m} />
                  ))}
                  {sec.key === 'Products' && sec.itemsPage.map((p) => {
                    const full = productMap.get(p.product_id);
                    if (!full) return null;
                    return (
                      <TopProductListItem
                        key={`prod-${p.product_id}-p${sec.page}`}
                        product={{ ...full, machine_number: p.machine_number }}
                        machine_id={p.machine_id}
                        source="search_results"
                      />
                    );
                  })}
                  {sec.key === 'Orders' && sec.itemsPage.map((o, idx) => (
                    <OrderListItem key={`order-${o.product_id}-${idx}-p${sec.page}`} order={o} />
                  ))}
                  {sec.key === 'Pages' && sec.itemsPage.map((p, idx) => (
                    <PageListItem
                      key={`page-${idx}-p${sec.page}`}
                      page={p}
                      onNavigate={(page) => {
                        if (page?.href === ROUTES.DOCUMENT || page?.name === "Documents") {
                          handleDocumentationNavigate();
                          return;
                        }
                        navigate(page?.href);
                      }}
                    />
                  ))}
                  {sec.key === 'Documents' && sec.itemsPage.map((doc) => (
                    <DocCard
                      key={`doc-${doc.product_id}-p${sec.page}`}
                      title={doc.name || 'Document'}
                      subtitle={getDocSubtitle(doc, t)}
                      chipLabel={getDocChipLabel(doc)}
                      imageProps={{
                        path: doc.attachment_type === 'file' ? doc.attachment : undefined,
                        url: doc.attachment_type === 'url' ? doc.attachment : undefined,
                        type: doc.attachment_type,
                      }}
                      rightContent={
                        <Button
                          size="small"
                          variant="text"
                          endIcon={<Note />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDocumentationNavigate();
                          }}
                        >
                          {t('UI_TEXT.VIEW_IN_DOCUMENTS')}
                        </Button>
                      }
                      cardProps={{
                        elevation: 0,
                        onClick: () => handleDocumentationNavigate(),
                        sx: {
                          width: '100%',
                          borderRadius: 2,
                          border: 'none',
                          backgroundColor: '#fff',
                          boxShadow: '0 0 6px 1px rgba(0,0,0,0.1)',
                          my: 0.75,
                          px: 1,
                          '&:hover': {
                            boxShadow: '0 0 10px 2px rgba(0,0,0,0.2)',
                          },
                          cursor: 'pointer',
                        },
                      }}
                    />
                  ))}
                  {sec.key === 'DocumentCategories' && sec.itemsPage.map((cat) => (
                    <Box
                      key={`doc-cat-${cat.name}-p${sec.page}`}
                      sx={{
                        width: '100%',
                        maxHeight: '75px',
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: '#fff',
                        boxShadow: '0 0 6px 1px rgba(0,0,0,0.1)',
                        p: 2,
                        columnGap: 2,
                        margin: '7px 0',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleDocumentCategoryNavigate(cat.name)}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 'bold',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {cat.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: '11px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {cat.count} {t('UI_TEXT.DOCUMENTS')}
                        </Typography>
                      </Box>
                      <Button size="small" sx={{ flexShrink: 0 }}>
                        {t('UI_TEXT.VIEW_IN_DOCUMENTS')}
                      </Button>
                    </Box>
                  ))}
                </Box>

                {/* Pagination */}
                {sec.items.length > pageSize && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Pagination
                      size="small"
                      count={Math.ceil(sec.items.length / pageSize)}
                      page={sec.page}
                      onChange={(_, val) => sec.setPage(val)}
                    />
                  </Box>
                )}
                </Box>
              ))}
              {row.length === 1 && (
                <Box sx={{ flex: 1 }} />
              )}
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
};

export default SearchResults;
